import { ApolloError } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { NAV_PATHS } from '@modules/common/constants';
import { useGetArtefactScope } from '@modules/common/hooks';
import { useUpdateUser } from '@modules/common/hooks/useUpdateUser';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorCode from 'v2/core/errors/codes';
import { isErrorCode } from 'v2/core/errors/errors';
import { useGetUser, useGetUserSetter } from 'v2/core/services/user';
import { useDashboardStore } from '../store/useDashboardStore';
import { UpdateDashboardRequest } from '../types';
import { useCreateDashboard } from './useCreateDashboard';
import { useDuplicateAndUpdateDashboard } from './useDuplicateAndUpdateDashboard';
import { initMetadataV2 } from './useFetchDashboard/helpers/initMetadataV2';
import { useUpdateDashboard } from './useUpdateDashboard';

interface useUpsertDashboardParams {
  shouldSkipRedirect?: boolean;
}
interface upsertDashboardParams {
  dashboardProps: Partial<UpdateDashboardRequest>;
  shouldRefetchAnalyses?: boolean;
  shouldRefetchDashboard?: boolean;
}

export const useUpsertDashboard = ({
  shouldSkipRedirect = false,
}: useUpsertDashboardParams = {}) => {
  const user = useGetUser();
  const navigate = useNavigate();
  const appSetUser = useGetUserSetter();
  const { updateUser } = useUpdateUser();
  const { showErrorNotification } = useNotificationContext();
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const isHomeContext = useDashboardStore(
    ({ visual }) => visual.context === 'Home',
  );
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const { updateDashboard, loading: updateLoading } = useUpdateDashboard();
  const metadata = useDashboardStore(({ metadata }) => metadata);
  const artefactScope = useGetArtefactScope();
  const [duplicateAndUpdateDashboard, { loading: isDuplicateRequestLoading }] =
    useDuplicateAndUpdateDashboard({
      onCompleted: (newDashboard) => {
        if (newDashboard) {
          dispatch({
            type: DashboardActionType.SET_DASHBOARD,
            payload: newDashboard,
          });
          initMetadataV2({
            dashboard: newDashboard,
            dispatch,
            cellMinimumHeightsByRowID: {},
          });

          appSetUser({
            ...dependenciesRef.current.user,
            homepageDashboardID: newDashboard.id,
          });
          updateUser({
            homepageDashboardID: newDashboard.id,
          });
        }
      },
    });

  const dependencies = { dashboard, user };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  const onCreate = useCallback(
    (dashboard: { viewToken: string }) => {
      if (shouldSkipRedirect) {
        return;
      }

      const dashboardRoute = `/${NAV_PATHS.DASHBOARD}/${dashboard.viewToken}`;
      navigate(dashboardRoute);
    },
    [navigate, shouldSkipRedirect],
  );

  const { createDashboard, loading: createLoading } = useCreateDashboard({
    onCreate,
  });

  const upsertDashboard = useCallback(
    async ({
      dashboardProps: dashboardPropsToBeUpdated,
      shouldRefetchAnalyses,
    }: upsertDashboardParams) => {
      //this variable will be changed to const after contract update
      const dashboard = dependenciesRef.current.dashboard;
      const isDefaultDashboard =
        dependenciesRef.current.user.tenant.dashboardSettings
          .homepageDashboardID === dashboard.id;

      const getSavedDashboard = async () => {
        if (!dashboard.id) {
          const { data } = await createDashboard({
            variables: {
              ...dashboardPropsToBeUpdated,
              scope: artefactScope,
              analysisIDs:
                dashboardPropsToBeUpdated.analysisIDs ??
                dashboardPropsToBeUpdated.addAnalysisIDs,
              title: dashboardPropsToBeUpdated.title ?? dashboard.title ?? '',
              metadata: dashboardPropsToBeUpdated.metadata ?? '',
              shortMetadata:
                dashboardPropsToBeUpdated.shortMetadata ??
                dashboard.shortMetadata,
              tenantID: dependenciesRef.current.user.tenant.id,
              createdBy: dependenciesRef.current.user.id,
            },
          });

          return data?.createDashboard?.dashboard;
        }

        if (isHomeContext && isDefaultDashboard) {
          const addAnalysisIDs = dashboardPropsToBeUpdated.addAnalysisIDs
            ? Array.isArray(dashboardPropsToBeUpdated.addAnalysisIDs)
              ? dashboardPropsToBeUpdated.addAnalysisIDs
              : [dashboardPropsToBeUpdated.addAnalysisIDs]
            : undefined;

          const removeAnalysisIDs = dashboardPropsToBeUpdated.removeAnalysisIDs
            ? Array.isArray(dashboardPropsToBeUpdated.removeAnalysisIDs)
              ? dashboardPropsToBeUpdated.removeAnalysisIDs
              : [dashboardPropsToBeUpdated.removeAnalysisIDs]
            : undefined;

          return await duplicateAndUpdateDashboard({
            duplicateAndUpdateDashboardInput: {
              dashboardID: dependenciesRef.current.dashboard.id,
              shouldDuplicateAnalyses: true,
              updateParams: {
                addAnalysisIDs,
                removeAnalysisIDs,
                addWidget: dashboardPropsToBeUpdated.addWidget,
                updateWidget: dashboardPropsToBeUpdated.updateWidget,
                deleteWidget: dashboardPropsToBeUpdated.deleteWidget,
                filterClause: dashboardPropsToBeUpdated.filterClause,
                metadataV2: {
                  ...metadata,
                  ...dashboardPropsToBeUpdated.metadataV2,
                },
              },
            },
            shouldRefetchAnalyses,
          });
        }

        const resp = await updateDashboard({
          updateRequest: {
            ...dashboardPropsToBeUpdated,
            metadataV2: {
              ...metadata,
              ...dashboardPropsToBeUpdated.metadataV2,
            },
            id: dashboard.id,
            editToken: dashboard.editToken ?? '',
            viewToken: dashboard.viewToken ?? '',
          },
          shouldRefetchAnalyses,
        });

        return resp?.data?.updateDashboard?.dashboard;
      };

      try {
        const updatedDashboard = await getSavedDashboard();

        if (updatedDashboard) {
          dispatch({
            type: DashboardActionType.SET_DASHBOARD,
            payload: updatedDashboard,
          });
        }

        return updatedDashboard;
      } catch (error) {
        if (
          error instanceof ApolloError &&
          isErrorCode(error, ErrorCode.UnprocessableEntity)
        ) {
          return;
        }

        showErrorNotification({
          contentComponent: 'Failed to upsert Dashboard.',
          props: { isClosable: true },
        });
      }
    },
    [
      updateDashboard,
      metadata,
      createDashboard,
      artefactScope,
      dispatch,
      showErrorNotification,
      isHomeContext,
      duplicateAndUpdateDashboard,
    ],
  );

  return {
    upsertDashboard,
    loading: updateLoading || createLoading || isDuplicateRequestLoading,
  };
};
